import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { captureError } from '@/lib/sentry';

interface SendDocumentParams {
  documentType: 'estimate' | 'invoice' | 'change_order';
  documentId: string;
  versionId?: string;
  recipientEmail: string;
  recipientName?: string;
  subject: string;
  bodyHtml: string;
  pdfBase64: string;
  pdfFilename: string;
  companyId: string;
}

export const useSendDocument = () => {
  const queryClient = useQueryClient();
  const [isSending, setIsSending] = useState(false);

  const sendDocumentMutation = useMutation({
    mutationFn: async (params: SendDocumentParams) => {
      setIsSending(true);
      
      const { data, error } = await supabase.functions.invoke('send-document', {
        body: params,
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      return data;
    },
    onSuccess: (data, variables) => {
      toast.success(`${variables.documentType === 'estimate' ? 'Estimate' : 'Invoice'} sent successfully to ${variables.recipientEmail}`);
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['document-sends', variables.documentType, variables.documentId] });
      queryClient.invalidateQueries({ queryKey: [variables.documentType === 'estimate' ? 'estimates' : 'invoices'] });
      queryClient.invalidateQueries({ queryKey: [variables.documentType, variables.documentId] });
      
      setIsSending(false);
    },
    onError: (error: Error, variables) => {
      console.error('Error sending document:', error);
      captureError(error, { feature: 'document-send', action: variables.documentType });
      toast.error(`Failed to send ${variables.documentType}: ${error.message}`);
      setIsSending(false);
    },
  });

  return {
    sendDocument: sendDocumentMutation.mutate,
    sendDocumentAsync: sendDocumentMutation.mutateAsync,
    isSending: isSending || sendDocumentMutation.isPending,
    isError: sendDocumentMutation.isError,
    error: sendDocumentMutation.error,
  };
};
